import React, { useEffect, useState } from "react";
import Highcharts from "highcharts";
import { useParams } from "react-router-dom";
import { FaSquareFull } from "react-icons/fa6";
import HighchartsReact from "highcharts-react-official";
import {
  Box,
  Grid2,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Typography,
  useTheme,
  Skeleton,
} from "@mui/material";
import { Circle } from "@mui/icons-material";
import { IoIosArrowDown } from "react-icons/io";
import { useGetAllManpowerPlanActualQuery } from "store/slices/apis/manpowerDashboardSlice";



const ManpowerPlanActual = () => {


  const [chartData, setChartData] = useState([]);
  const gridItemProps = { xs: 12, sm: 6, md: 3 };
  const theme = useTheme();
  const { projectId } = useParams();
  const { data, isLoading, refetch } = useGetAllManpowerPlanActualQuery({ projectId });

  const rawPlanned = data?.data?.totalPlannedManpower ?? 0;
  const rawActual = data?.data?.totalActualManpower ?? 0;
  const rawVariance = data?.data?.variation ?? 0;

  const planned = Number(rawPlanned.toFixed(2));
  const actual = Number(rawActual.toFixed(2));
  const variance = Number(rawVariance.toFixed(2));
  const plannedPercent = 100;
  const actualPercent = planned ? Number(((actual / planned) * 100).toFixed(2)) : 0;
  const variancePercent = planned ? Number(((Math.abs(variance) / planned) * 100).toFixed(2)) : 0;



  useEffect(() => {
    if (data) {



      const updatedChartData = [

        {
          name: "Planned Progress",
          data: [
            {
              y: plannedPercent,
              color: "#295477",
              radius: "75%",
              innerRadius: "85%",
            },
          ],
        },
        {
          name: "Actual Progress",
          data: [
            {
              y: actualPercent,
              color: "#E72B33",
              radius: "90%",
              innerRadius: "100%",
            },
          ],
        },
        {
          name: "Variance",
          data: [
            {
              y: variancePercent,
              color: "#FACC15",
              radius: "60%",
              innerRadius: "70%",
            },
          ],
        },
      ];

      setChartData(updatedChartData);
    }
  }, [data]);


  let options = {
    chart: {
      type: "solidgauge",
      height: 130,
      width: 130,
      backgroundColor: 'transparent'
    },
    title: {
      text: "",
      style: { fontSize: "18px" },
    },
    pane: {
      size: '100%',
      startAngle: 0,
      endAngle: 360,
      background: [
        {
          outerRadius: "",
          innerRadius: "88%",
          backgroundColor: Highcharts.color("#7cb5ec").setOpacity(0.3).get(),
          borderWidth: 0,
        },
        {
          outerRadius: "",
          innerRadius: "63%",
          backgroundColor: Highcharts.color("#90ed7d").setOpacity(0.3).get(),
          borderWidth: 0,
        },
        {
          outerRadius: "",
          innerRadius: "38%",
          backgroundColor: Highcharts.color("#f7a35c").setOpacity(0.3).get(),
          borderWidth: 0,
        },
      ],
    },
    yAxis: {
      min: 0,
      max: 100,
      lineWidth: 0,
      tickPositions: [],
    },
    plotOptions: {
      solidgauge: {
        dataLabels: {
          enabled: false,
        },
        linecap: "round",
        stickyTracking: false,
      },
    },
    tooltip: {
      enabled: true,
      formatter: function () {
        return `<b>${this.series.name}</b>: ${this.y}%`;
      }
    },

    legend: {
      enabled: false,
      align: "center",
      verticalAlign: "bottom",
      layout: "horizontal",
    },
    series: chartData,
    credits: {
      enabled: false,
    },
  };

  return (
    <Box
      sx={{
        p: 2,
        width: "100%",
        maxWidth: "100%",
        height: { xs: "auto", md: "246px" },
        border: "1px solid #2525252E",
        mt: 0,
        borderRadius: "12px",
        overflow: "hidden",
        backgroundColor: "white",
      }}
    >
      <Box>
        <Typography
          variant="body"
          sx={{
            fontWeight: 500,
            fontSize: "18px",
            lineHeight: "100%",
            letterSpacing: "-0.4px",
            color: `${theme?.customSettings?.themeColor}`,
          }}
        >
          Manpower Plan V/s Actual
        </Typography>
        {/* <Typography
          sx={{
            mt: "5px",
            color: "#5052588A",
            fontWeight: 500,
            fontSize: "14px",
            lineHeight: "100%",
            letterSpacing: "-0.4px",
          }}
        >
          Get a holistic view of how work is being prioritized
        </Typography> */}
      </Box>
      {isLoading ? (
        <Box sx={{ display: "flex", mt: 3, justifyContent: "space-between", alignItems: "center" }}>
          <List sx={{ padding: 0, mb: 4 }}>
            {[1, 2, 3].map((_, i) => (
              <ListItem key={i} sx={{ paddingLeft: 0 }}>
                <Skeleton variant="circular" width={14} height={14} sx={{ mr: 1 }} />
                <Skeleton variant="text" width={120} height={20} />
              </ListItem>
            ))}
          </List>
          <Skeleton variant="circular" width={130} height={130} />
        </Box>
      ) : chartData.length === 0 ||
        chartData.every(series => series?.data?.[0]?.y === 0) ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 150,
            mt: 2,
            color: "#9E9E9E",
            fontSize: "16px",
          }}
        >
          No data available to display the chart.
        </Box>
      ) : (
        <Grid2 container spacing={2} alignItems="center">
          <Grid2 item xs={12} md={6}>
            <List sx={{ padding: 0, mb: { xs: 0, md: 4 } }}>
              {chartData.map((val, index) => (
                <ListItem key={index} sx={{ paddingLeft: 0 }}>
                  <ListItemIcon sx={{ minWidth: "20px" }}>
                    <FaSquareFull
                      style={{
                        color: `${val?.data[0]?.color}`,
                        height: "14px",
                        width: "14px",
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <>
                        <Typography
                          component="span"
                          sx={{
                            whiteSpace: "nowrap",
                            fontWeight: 500,
                            fontSize: "16px",
                            lineHeight: "100%",
                            letterSpacing: "-0.4px",
                          }}
                        >
                          {val?.name}:{" "}
                        </Typography>
                        <Typography
                          component="span"
                          sx={{
                            color: "#182A4E",
                            whiteSpace: "nowrap",
                            fontWeight: 500,
                            fontSize: "16px",
                            lineHeight: "100%",
                            letterSpacing: "-0.4px",
                          }}
                        >
                          {val?.data[0]?.y}%
                        </Typography>
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Grid2>

          <Grid2
            item
            xs={12}
            md={6}
            sx={{
              display: "flex",
              justifyContent: "center",
              height: { xs: "auto", md: "100%" },
              overflow: "visible",
              p: { xs: 2, md: 0 },
            }}
          >
            <Box
              sx={{
                flexShrink: 0,
                width: { xs: "100%", md: "auto" },
                maxWidth: { xs: 200, md: "100%" },
              }}
            >
              <HighchartsReact highcharts={Highcharts} options={options} />
            </Box>
          </Grid2>
        </Grid2>
      )}
    </Box>
  );
};

export default ManpowerPlanActual;